import { motion } from "framer-motion";
import type { TourStep } from "./types";

type Props = {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  position: "above" | "below";
  targetRect: { x: number; y: number; width: number; height: number } | null;
  onNext: () => void;
  onBack: () => void;
  onDone: () => void;
  onSkip: () => void;
  isFirst: boolean;
  isLast: boolean;
};

export function TourTooltip({
  step,
  stepIndex,
  totalSteps,
  position,
  targetRect,
  onNext,
  onBack,
  onDone,
  onSkip,
  isFirst,
  isLast,
}: Props) {
  const gap = 12;
  const safeTop = 60; // Below MobileHeader (h-12 + safe-top)

  let usedPosition = position;

  if (position === "above" && targetRect) {
    // If above would go off-screen, flip to below
    if (targetRect.y - gap < safeTop + 200) {
      usedPosition = "below";
    }
  }

  // Use top/bottom CSS for positioning instead of transform: translateY(-100%)
  // to avoid conflicts with framer-motion's y animation overriding our transform.
  const positionStyle: React.CSSProperties = {};

  if (usedPosition === "below" && targetRect) {
    positionStyle.top = targetRect.y + targetRect.height + gap;
  } else if (usedPosition === "above" && targetRect) {
    positionStyle.bottom = window.innerHeight - (targetRect.y - gap);
  } else {
    positionStyle.top = window.innerHeight / 2;
  }

  const hasTarget = !!targetRect;
  const slideOffset = usedPosition === "below" ? 8 : -8;

  return (
    <motion.div
      layout
      key={stepIndex}
      initial={{ opacity: 0, ...(hasTarget ? { y: slideOffset } : {}) }}
      animate={{ opacity: 1, ...(hasTarget ? { y: 0 } : {}) }}
      exit={{ opacity: 0, ...(hasTarget ? { y: slideOffset } : {}) }}
      transition={{
        layout: { type: "spring", stiffness: 350, damping: 30 },
        opacity: { duration: 0.2 },
        y: { type: "spring", stiffness: 350, damping: 30 }
      }}
      className="fixed left-4 right-4 z-[72] mx-auto max-w-[340px]"
      style={{
        ...positionStyle,
        ...(!hasTarget ? { transform: "translateY(-50%)" } : {}),
      }}
    >
      <motion.div layout className="rounded-2xl bg-bg shadow-xl border border-border/50 p-5">
        {/* Progress dots + counter */}
        <motion.div layout className="flex items-center gap-2 mb-3">
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }, (_, i) => (
              <motion.div
                layout
                key={i}
                className="h-1 rounded-full"
                initial={false}
                animate={{
                  width: i === stepIndex ? 20 : 6,
                  backgroundColor: i === stepIndex ? "#2D5A4A" : "hsl(var(--border))",
                }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            ))}
          </div>
          <span className="text-xs text-muted ml-auto">
            {stepIndex + 1} of {totalSteps}
          </span>
        </motion.div>

        {/* Content */}
        <motion.h3 layout className="text-lg font-semibold font-display text-foreground mb-1">
          {step.title}
        </motion.h3>
        <motion.p layout className="text-sm text-muted leading-relaxed mb-4">
          {step.description}
        </motion.p>

        {/* Navigation */}
        <motion.div layout className="flex items-center justify-between">
          <div>
            {!isFirst && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="px-4 py-2 text-sm font-medium text-muted hover:text-foreground transition-colors rounded-lg"
              >
                Back
              </motion.button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onSkip}
              className="text-xs text-muted hover:text-foreground transition-colors"
            >
              Skip
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={isLast ? onDone : onNext}
              className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: "#2D5A4A" }}
            >
              {isLast ? "Done" : "Next"}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
